import type {
  SiteResponse,
  SiteTemplateResponse,
} from '@lms/api-client/backend';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { useTranslations } from 'use-intl';
import { SimpleSelect } from '@/components/simple-select';
import { SubmitButton } from '@/components/submit-button';
import { ShareLocationAccess } from '@/features/access-matrix/share-location-access';
import { useBrand } from '@/features/branding/brand-provider';
import { addLocation } from './locations-api';

// Ports _components/add-location.tsx. The conform + server-action form becomes a
// controlled form calling `addLocation`; on success it revalidates the list SWR
// key and navigates back (the Next action `redirect`ed) — after a share step,
// since a new location is visible to nobody but super-admins until shared.
export function AddLocation({
  templates,
}: {
  templates: SiteTemplateResponse[];
}) {
  const t = useTranslations('addlocation');
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { brand: site, slug } = useBrand();

  const [name, setName] = useState('');
  const [locationSlug, setLocationSlug] = useState('');
  const [template, setTemplate] = useState('');
  const [pending, setPending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    slug?: string;
    template?: string;
  }>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [created, setCreated] = useState<SiteResponse | null>(null);

  const finish = async () => {
    await mutate(['admin/locations', site.id]);
    navigate(`/${slug}/admin/locations`);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setFieldErrors({});
    setFormErrors([]);
    try {
      const result = await addLocation({
        portfolio: site,
        name,
        slug: locationSlug,
        template,
      });
      if (result.status === 'success') {
        setCreated(result.location);
        return;
      }
      setFieldErrors(result.fieldErrors ?? {});
      setFormErrors(result.formErrors ?? []);
    } finally {
      setPending(false);
    }
  };

  if (created) {
    return (
      <ShareLocationAccess
        portfolioId={site.id}
        location={created}
        onDone={finish}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('add_location')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="name">{t('name')}</Label>
            <Input
              id="name"
              name="name"
              placeholder={t('name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {fieldErrors.name && (
              <p className="text-destructive text-sm">{fieldErrors.name}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="slug">{t('slug')}</Label>
            <Input
              id="slug"
              name="slug"
              placeholder={t('slug')}
              value={locationSlug}
              onChange={(e) => setLocationSlug(e.target.value)}
            />
            {fieldErrors.slug && (
              <p className="text-destructive text-sm">{fieldErrors.slug}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="template">{t('template')}</Label>
            <SimpleSelect
              id="template"
              name="template"
              resetLabel={t('none')}
              placeholder={t('none')}
              value={template}
              onValueChange={setTemplate}
              options={templates.map((tpl) => ({
                value: `${tpl.id}`,
                label: tpl.name,
              }))}
            />
            <p className="text-muted-foreground text-sm">
              {t('template_help')}
            </p>
            {fieldErrors.template && (
              <p className="text-destructive text-sm">{fieldErrors.template}</p>
            )}
          </div>
          {formErrors.length > 0 && (
            <div className="text-destructive text-sm">
              {formErrors.map((error) => (
                <p key={error}>{error}</p>
              ))}
            </div>
          )}
          <SubmitButton label={t('add_location')} pending={pending} />
        </form>
      </CardContent>
    </Card>
  );
}
